// src/pages/b2b/sections/ProductDetailB2BTop.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import ArticleIcon from "@mui/icons-material/Article";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../Auth/AuthContext";
import { resolveAssetUrl } from "../../../../../lib/http";
import { useGlobalSnackbar } from "../../../../GlobalFunction/GlobalSnackbarProvider";
import { resolveCanonicalUserID } from "../../../../../Utils/UserIdentity";
import { InquiryAPI } from "../../../../../api/Inquiry";
import { FavouriteAPI } from "../../../../../api/Favourite";

function normalizeLang(lang) {
  const s = String(lang || "")
    .toUpperCase()
    .trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

const I18N_TOP = {
  ZH: {
    micrositeLabel: "特設網站：",
    brandSiteButton: (brand) => `品牌網站（${brand || "品牌"}）`,
    variantALabel: "規格：",
    variantBLabel: "重量/容量：",
    variantCLabel: "尺寸：",
    inquiryBtn: "詢價",
    inquiryBtnMsg: "申請樣品前，請先登入。",
    addingBtn: "加入中…",
    skuLabel: "編號：",
    noProductId: "無法取得商品編號，請稍後再試。",
    added: "已加入詢價清單！",
    addFailPrefix: (msg) => `加入詢價失敗：${msg}`,

    favAdd: "加入收藏",
    favRemove: "取消收藏",
    favNeedLogin: "請先登入才能收藏商品。",
    favAdded: "已加入收藏！",
    favRemoved: "已取消收藏！",
    favFailedPrefix: (msg) => `收藏操作失敗：${msg}`,

    // ✅ new dialog translations
    loginTitle: "需要登入",
    cancel: "取消",
    login: "登入",
  },
  EN: {
    micrositeLabel: "Featured Site:",
    brandSiteButton: (brand) => `Brand Website (${brand || "Brand"})`,
    variantALabel: "Specifications:",
    variantBLabel: "Weight/Capacity:",
    variantCLabel: "Dimensions:",
    inquiryBtn: "Inquire",
    inquiryBtnMsg: "Please sign in before requesting samples.",
    addingBtn: "Adding…",
    skuLabel: "Item No.:",
    noProductId: "Unable to get product ID. Please try again later.",
    added: "Added to inquiry list!",
    addFailPrefix: (msg) => `Failed to add to inquiry list: ${msg}`,

    favAdd: "Add to wishlist",
    favRemove: "Remove from wishlist",
    favNeedLogin: "Please log in to add to wishlist.",
    favAdded: "Added to wishlist!",
    favRemoved: "Removed from wishlist!",
    favFailedPrefix: (msg) => `Wishlist action failed: ${msg}`,

    // ✅ new dialog translations
    loginTitle: "Login required",
    cancel: "Cancel",
    login: "Log in",
  },
  JP: {
    micrositeLabel: "特設サイト：",
    brandSiteButton: (brand) => `ブランドサイト（${brand || "ブランド"}）`,
    variantALabel: "仕様：",
    variantBLabel: "重量/容量：",
    variantCLabel: "寸法：",
    inquiryBtn: "見積り依頼書",
    inquiryBtnMsg: "サンプル請求には、ログインが必要です。",
    addingBtn: "追加中…",
    skuLabel: "品番：",
    noProductId: "商品IDを取得できません。しばらくしてから再度お試しください。",
    added: "見積リストに追加しました！",
    addFailPrefix: (msg) => `追加に失敗しました：${msg}`,

    favAdd: "お気に入りに追加",
    favRemove: "お気に入り解除",
    favNeedLogin: "お気に入り機能を使うにはログインしてください。",
    favAdded: "お気に入りに追加しました！",
    favRemoved: "お気に入りを解除しました！",
    favFailedPrefix: (msg) => `お気に入り操作に失敗しました：${msg}`,

    // ✅ new dialog translations
    loginTitle: "ログインが必要です",
    cancel: "キャンセル",
    login: "ログイン",
  },
};

function publicUrl(path) {
  let base = "/";
  if (typeof process !== "undefined" && process.env?.PUBLIC_URL)
    base = process.env.PUBLIC_URL;
  return (
    (base.replace(/\/+$/, "") || "") +
    "/" +
    String(path || "").replace(/^\/+/, "")
  );
}

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

function hasVariantMatch(variantsAll, { a, b, c }) {
  return (variantsAll || []).some((v) => {
    if (a && String(v.variantA || "") !== String(a)) return false;
    if (b && String(v.variantB || "") !== String(b)) return false;
    if (c && String(v.variantC || "") !== String(c)) return false;
    return true;
  });
}

function formatBulletLines(text) {
  if (text == null) return "";
  const s = String(text);

  let out = s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  out = out.replace(/([ \t])([●○・▪▫■□◆◇★☆►▶▸▹➤✓✔✦✧•])/g, "$1\n$2");
  out = out.replace(/^([ \t]*)([●○・▪▫■□◆◇★☆►▶▸▹➤✓✔✦✧•])/gm, "\n$1$2");
  out = out.replace(/(^|[ \t])(\d{1,3}\s*(?:[.)、．]))/gm, "$1\n$2");
  out = out.replace(/(^|[ \t])(\(\s*\d{1,3}\s*\))/gm, "$1\n$2");
  out = out.replace(/(^|[ \t])([A-Za-z]\s*(?:[.)、．]))/gm, "$1\n$2");
  out = out.replace(/(^|[ \t])(\(\s*[A-Za-z]\s*\))/gm, "$1\n$2");

  out = out.replace(/\n{3,}/g, "\n\n");
  return out.trim();
}

/** prevent infinite onError loops */
const FALLBACK_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

function getWpHeadlessUploadFallback(src) {
  const raw = String(src || "").trim();
  if (!raw) return "";

  try {
    const u = new URL(raw);

    if (u.pathname.startsWith("/wp-headless/wp-content/uploads/")) {
      u.pathname = u.pathname.replace(
        /^\/wp-headless\/wp-content\/uploads\//,
        "/wp-content/uploads/",
      );
      return u.toString();
    }

    if (u.pathname.startsWith("/wp-content/uploads/")) {
      u.pathname = `/wp-headless${u.pathname}`;
      return u.toString();
    }
  } catch {
    if (raw.startsWith("/wp-headless/wp-content/uploads/")) {
      return raw.replace(
        /^\/wp-headless\/wp-content\/uploads\//,
        "/wp-content/uploads/",
      );
    }

    if (raw.startsWith("/wp-content/uploads/")) {
      return `/wp-headless${raw}`;
    }
  }

  return "";
}

function applyImgFallback(e, placeholder) {
  const img = e.currentTarget;
  const currentSrc = String(img?.src || "");

  if (currentSrc.endsWith("/img/empty.webp")) return;
  if (currentSrc === placeholder) return;

  const triedUploadAltFallback = img.dataset.triedUploadAltFallback === "1";
  const fallback = getWpHeadlessUploadFallback(currentSrc);

  if (!triedUploadAltFallback && fallback) {
    img.dataset.triedUploadAltFallback = "1";
    img.src = fallback;
    return;
  }

  if (img.dataset.fallbackApplied === "1") return;
  img.dataset.fallbackApplied = "1";
  img.onerror = null;
  img.src = placeholder || FALLBACK_PIXEL;
}

/** Try to extract URL from string/object like ProductCard */
function toUrl(v) {
  if (!v) return undefined;
  if (typeof v === "string") return v;

  if (typeof v === "object") {
    if (v.url) return v.url;
    if (v.image) return v.image;
    if (v.path) return v.path;

    if (v.sourceUrl) return v.sourceUrl;
    if (v.mediaItemUrl) return v.mediaItemUrl;

    if (v.node && (v.node.sourceUrl || v.node.mediaItemUrl))
      return v.node.sourceUrl || v.node.mediaItemUrl;
  }
  return undefined;
}

function resolveUserID(user) {
  return resolveCanonicalUserID(user);
}

function isAuthed(user) {
  if (!user) return false;
  if (user?.isLoggedIn === true) return true;
  if (user?.loginStatus === true) return true;
  return !!resolveUserID(user);
}

function Top({
  product,
  override = {},
  variantOptions,
  selectedA,
  selectedB,
  selectedC,
  onSelectA,
  onSelectB,
  onSelectC,
  variantsAll = [],
  lang,
}) {
  const { showSnackbar } = useGlobalSnackbar();
  const navigate = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();

  const loggedIn = isAuthed(user);

  const L = normalizeLang(lang);
  const t = I18N_TOP[L] || I18N_TOP.ZH;

  // ✅ login dialog state
  const [loginOpen, setLoginOpen] = useState(false);

  const openLoginDialog = () => setLoginOpen(true);
  const closeLoginDialog = () => setLoginOpen(false);

  // ✅ SAME image strategy as ProductInfoPageB2B/ProductCard:
  const safeImages = useMemo(() => {
    const arr = Array.isArray(product?.images) ? product.images : [];

    const urls = arr
      .map(toUrl)
      .filter(Boolean)
      .map((u) => resolveAssetUrl(String(u)))
      .filter(Boolean);

    const seen = new Set();
    const out = [];
    for (const u of urls) {
      if (!seen.has(u)) {
        seen.add(u);
        out.push(u);
      }
    }

    return out.length ? out.slice(0, 10) : [];
  }, [product?.images]);

  const placeholder = publicUrl("img/empty.webp");
  const [idx, setIdx] = useState(0);
  const [start, setStart] = useState(0);
  const [adding, setAdding] = useState(false);
  const VISIBLE = 5;

  // favourite local state + busy lock
  const [favBusy, setFavBusy] = useState(false);
  const [fav, setFav] = useState(
    () => !!(product?.favourite === 1 || product?.favourite === true),
  );

  useEffect(() => {
    setFav(!!(product?.favourite === 1 || product?.favourite === true));
    setFavBusy(false);
  }, [product?.id, product?.productID, product?.favourite]);

  useEffect(() => {
    const clamped = clamp(idx, 0, Math.max(0, safeImages.length - 1));
    if (clamped !== idx) setIdx(clamped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeImages.length]);

  const end = start + VISIBLE;
  const windowed = useMemo(
    () => safeImages.slice(start, end),
    [safeImages, start, end],
  );

  const goPrevMain = () =>
    setIdx((i) => (i === 0 ? safeImages.length - 1 : i - 1));
  const goNextMain = () =>
    setIdx((i) => (i === safeImages.length - 1 ? 0 : i + 1));
  const jumpTo = (globalIdx) => {
    setIdx(globalIdx);
    const wantedStart =
      globalIdx < start
        ? globalIdx
        : globalIdx >= start + VISIBLE
          ? globalIdx - VISIBLE + 1
          : start;
    if (wantedStart !== start) setStart(wantedStart);
  };

  const name =
    override?.name ||
    product?.productName ||
    product?.info?.name ||
    product?.name ||
    "";

  const variantName =
    product?.variantName ||
    product?.main?.variantName ||
    product?.main?.["產品變體名稱"] ||
    "";

  const price = product?.price || "";

  const brand = product?.Brand ||
    product?.brand || {
      brandName: "saraya",
      brandID: "saraya",
    };

  const brandHref = brand?.brandLink ? brand?.brandLink : undefined;

  const resolveProductID = (p) =>
    p?.productID ??
    p?.id ??
    p?.ProductID ??
    p?.sku ??
    p?.main?.["編號"] ??
    null;

  const redirectToAuth = () => {
    const path = loc?.pathname || "/auth";
    const search = loc?.search || "";
    const hash = loc?.hash || "";
    const redirectTo = `${path}${search}${hash}`;

    navigate("/auth", {
      replace: false,
      state: { redirectTo },
    });
  };

  // ✅ updated: show dialog instead of immediate redirect
  const addToCart = async () => {
    try {
      if (!loggedIn) {
        openLoginDialog();
        return;
      }

      const productID = resolveProductID(product);
      const userID = resolveUserID(user);

      if (!userID) {
        openLoginDialog();
        return;
      }

      if (!productID) {
        showSnackbar(t.noProductId, "error");
        return;
      }

      setAdding(true);

      await InquiryAPI.createInquiry({
        userID,
        items: [{ productID: String(productID), qty: 1 }],
        lang: normalizeLang(lang),
      });

      showSnackbar(t.added, "success");
      window.dispatchEvent(
        new CustomEvent("inquiry:updated", {
          detail: { reason: "added", productID },
        }),
      );
    } catch (e) {
      console.error(e);
      showSnackbar(
        t.addFailPrefix(e?.response?.data?.error || e.message),
        "error",
      );
    } finally {
      setAdding(false);
    }
  };

  // ✅ safety favourite toggle
  const toggleFavourite = async () => {
    if (favBusy) return;

    // hide UI when not logged in, but safety guard anyway
    if (!loggedIn) {
      redirectToAuth();
      return;
    }

    const productID = resolveProductID(product);
    const userID = resolveUserID(user);

    if (!userID) {
      redirectToAuth();
      return;
    }
    if (!productID) {
      showSnackbar(t.noProductId, "error");
      return;
    }

    const next = !fav;

    // optimistic UI
    setFav(next);
    setFavBusy(true);

    try {
      if (next) {
        await FavouriteAPI.add(userID, String(productID));
        showSnackbar(t.favAdded, "success");
      } else {
        await FavouriteAPI.remove(userID, String(productID));
        showSnackbar(t.favRemoved, "success");
      }

      window.dispatchEvent(
        new CustomEvent("favourite:updated", {
          detail: { productID: String(productID), favourite: next ? 1 : 0 },
        }),
      );
    } catch (e) {
      console.error(e);

      const code = e?.response?.status;
      if (code === 401 || code === 403) {
        setFav(!next);
        setFavBusy(false);
        redirectToAuth();
        return;
      }

      setFav(!next);

      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Unknown error";
      showSnackbar(t.favFailedPrefix(msg), "error");
    } finally {
      setFavBusy(false);
    }
  };

  const optionsA = variantOptions?.variantA || variantOptions?.weights || [];
  const optionsB = variantOptions?.variantB || variantOptions?.sizes || [];
  const optionsC = variantOptions?.variantC || [];

  const handleClickA = (opt) => {
    const isEmpty = !opt || String(opt).trim() === "";
    if (isEmpty) return;
    if (selectedA === opt) return onSelectA?.("");
    onSelectA?.(opt);
  };

  const handleClickB = (opt) => {
    const isEmpty = !opt || String(opt).trim() === "";
    if (isEmpty) return;
    if (selectedB === opt) return onSelectB?.("");
    onSelectB?.(opt);
  };

  const handleClickC = (opt) => {
    const isEmpty = !opt || String(opt).trim() === "";
    if (isEmpty) return;
    if (selectedC === opt) return onSelectC?.("");
    onSelectC?.(opt);
  };

  const frontDescRaw = product?.main?.frontDescription || "";
  const frontDesc = useMemo(
    () => formatBulletLines(frontDescRaw),
    [frontDescRaw],
  );

  const mainSrc = safeImages[idx] || "";

  const favTooltip = fav ? t.favRemove : t.favAdd;

  // ✅ Hide favourite button if not logged in
  const showFavBtn = loggedIn;

  return (
    <>
      <Grid
        container
        columnSpacing={{ xs: 0, md: 5 }}
        rowSpacing={{ xs: 2, md: 3 }}
        alignItems="flex-start"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          flexWrap: { xs: "wrap", md: "nowrap" },
          overflow: "visible",
        }}
      >
        {/* LEFT: main image + thumbs */}
        <Grid
          item
          xs={12}
          md="auto"
          sx={{
            flexBasis: { md: 420 },
            flexGrow: 0,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Box
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              width: "100%",
              maxWidth: { xs: "100%", sm: 480, md: "100%" },
              mx: { xs: "auto", md: 0 },
              height: { xs: "auto", md: 420 },
              minHeight: { xs: 220, sm: 260, md: 420 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={mainSrc || placeholder}
              alt={name}
              onError={(e) => applyImgFallback(e, placeholder)}
              sx={{
                width: "100%",
                height: { xs: "auto", md: "100%" },
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />

            {safeImages.length > 1 && (
              <>
                <IconButton
                  onClick={goPrevMain}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 8,
                    transform: "translateY(-50%)",
                    bgcolor: "background.paper",
                    boxShadow: 1,
                  }}
                >
                  <ChevronLeftRounded />
                </IconButton>
                <IconButton
                  onClick={goNextMain}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 8,
                    transform: "translateY(-50%)",
                    bgcolor: "background.paper",
                    boxShadow: 1,
                  }}
                >
                  <ChevronRightRounded />
                </IconButton>
              </>
            )}

            <Box
              sx={{
                position: "absolute",
                bottom: 8,
                left: "50%",
                transform: "translateX(-50%)",
                px: 1.25,
                py: 0.25,
                borderRadius: 999,
                bgcolor: "rgba(0,0,0,0.5)",
                color: "#fff",
                fontSize: 12,
              }}
            >
              {safeImages.length ? idx + 1 : 1} / {safeImages.length || 1}
            </Box>
          </Box>

          {/* thumbnails */}
          {safeImages.length > 1 && (
            <Box
              sx={{
                mt: 1.5,
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 0.5,
                  overflowX: "hidden",
                  overflowY: "hidden",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ minWidth: 0, maxWidth: "100%" }}
                >
                  {windowed.map((src, i) => {
                    const globalIdx = start + i;
                    const selected = globalIdx === idx;
                    const size = selected ? 68 : 56;
                    return (
                      <Box
                        key={`${src}-${globalIdx}`}
                        onClick={() => jumpTo(globalIdx)}
                        sx={{
                          flex: "0 0 auto",
                          width: size,
                          height: size,
                          borderRadius: 1,
                          p: 0.5,
                          cursor: "pointer",
                          border: "2px solid",
                          borderColor: selected ? "primary.main" : "divider",
                          bgcolor: "#fff",
                          transition: "all .15s ease",
                        }}
                      >
                        <Box
                          component="img"
                          src={src}
                          alt=""
                          onError={(e) => applyImgFallback(e, placeholder)}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 0.75,
                            display: "block",
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Box>
          )}
        </Grid>

        {/* RIGHT: info */}
        <Grid
          item
          xs={12}
          md
          zeroMinWidth
          sx={{
            flexGrow: 1,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Stack spacing={1.5} sx={{ minWidth: 0, maxWidth: "100%" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontFamily: "LINE Sans TW",
                lineHeight: 1.3,
                wordBreak: "break-word",
                maxWidth: "100%",
                fontSize: { xs: 20, sm: 22, md: 26, lg: 28 },
              }}
            >
              {name}
            </Typography>

            {variantName && (
              <Typography
                sx={{
                  fontFamily: "LINE Sans TW",
                  fontWeight: 500,
                  color: "text.secondary",
                  fontSize: { xs: 14, sm: 15 },
                  wordBreak: "break-word",
                }}
              >
                {variantName}
              </Typography>
            )}

            {!!price && (
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontFamily: "LINE Sans TW",
                  color: "primary.main",
                  fontSize: { xs: 18, sm: 19, md: 22 },
                }}
              >
                {price}
              </Typography>
            )}

            {!!frontDesc && (
              <Typography
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.8,
                  mt: 0.5,
                  wordBreak: "break-word",
                  maxWidth: "100%",
                  fontSize: { xs: 13, sm: 14, md: 15 },
                  whiteSpace: "pre-line",
                }}
              >
                {frontDesc}
              </Typography>
            )}

            <Typography sx={{ fontWeight: 700, fontSize: { xs: 14, md: 16 } }}>
              {t.skuLabel} {resolveProductID(product) || ""}
            </Typography>

            {brand?.brandLink && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <Typography
                  sx={{ fontWeight: 700, fontSize: { xs: 14, md: 16 } }}
                >
                  {t.micrositeLabel}
                </Typography>
                <Button
                  variant="contained"
                  endIcon={<OpenInNewRounded />}
                  href={brandHref}
                  disabled={!brandHref}
                  sx={{
                    fontSize: { xs: 14, md: 16 },
                    px: { xs: 2.2, md: 3 },
                    py: { xs: 0.7, md: 0.9 },
                    borderRadius: 999,
                    maxWidth: "100%",
                  }}
                >
                  {t.brandSiteButton(brand?.brandName)}
                </Button>
              </Box>
            )}

            {/* variantA */}
            {optionsA.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1,
                  flexWrap: "wrap",
                  maxWidth: "100%",
                }}
              >
                <Typography
                  sx={{ fontWeight: 700, fontSize: { xs: 14, md: 16 } }}
                >
                  {t.variantALabel}
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ minWidth: 0 }}
                >
                  {optionsA.map((opt) => {
                    const isEmpty = !opt || String(opt).trim() === "";
                    const isSelected = selectedA === opt;

                    const enabledCombo = hasVariantMatch(variantsAll, {
                      a: opt,
                      b: selectedB || null,
                      c: selectedC || null,
                    });

                    const disabled = isEmpty || !enabledCombo;

                    return (
                      <Button
                        key={opt}
                        size="small"
                        variant={isSelected ? "contained" : "outlined"}
                        color={isSelected ? "primary" : "inherit"}
                        disabled={disabled}
                        sx={{
                          fontSize: { xs: 12, md: 14 },
                          px: { xs: 1.8, md: 2.4 },
                          py: { xs: 0.5, md: 0.6 },
                          borderRadius: 999,
                          minWidth: 56,
                          opacity: disabled && !isSelected ? 0.4 : 1,
                        }}
                        onClick={() => !disabled && handleClickA(opt)}
                      >
                        {opt}
                      </Button>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* variantB */}
            {optionsB.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  maxWidth: "100%",
                }}
              >
                <Typography
                  sx={{ fontWeight: 700, fontSize: { xs: 14, md: 16 } }}
                >
                  {t.variantBLabel}
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ minWidth: 0 }}
                >
                  {optionsB.map((opt) => {
                    const isEmpty = !opt || String(opt).trim() === "";
                    const isSelected = selectedB === opt;

                    const enabledCombo = hasVariantMatch(variantsAll, {
                      a: selectedA || null,
                      b: opt,
                      c: selectedC || null,
                    });

                    const disabled = isEmpty || !enabledCombo;

                    return (
                      <Button
                        key={opt}
                        size="small"
                        variant={isSelected ? "contained" : "outlined"}
                        color={isSelected ? "primary" : "inherit"}
                        disabled={disabled}
                        sx={{
                          fontSize: { xs: 12, md: 14 },
                          px: { xs: 1.8, md: 2.4 },
                          py: { xs: 0.5, md: 0.6 },
                          borderRadius: 999,
                          minWidth: 56,
                          opacity: disabled && !isSelected ? 0.4 : 1,
                        }}
                        onClick={() => !disabled && handleClickB(opt)}
                      >
                        {opt}
                      </Button>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* variantC */}
            {optionsC.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  maxWidth: "100%",
                }}
              >
                <Typography
                  sx={{ fontWeight: 700, fontSize: { xs: 14, md: 16 } }}
                >
                  {t.variantCLabel}
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ minWidth: 0 }}
                >
                  {optionsC.map((opt) => {
                    const isEmpty = !opt || String(opt).trim() === "";
                    const isSelected = selectedC === opt;

                    const enabledCombo = hasVariantMatch(variantsAll, {
                      a: selectedA || null,
                      b: selectedB || null,
                      c: opt,
                    });

                    const disabled = isEmpty || !enabledCombo;

                    return (
                      <Button
                        key={opt}
                        size="small"
                        variant={isSelected ? "contained" : "outlined"}
                        color={isSelected ? "primary" : "inherit"}
                        disabled={disabled}
                        sx={{
                          fontSize: { xs: 12, md: 14 },
                          px: { xs: 1.8, md: 2.4 },
                          py: { xs: 0.5, md: 0.6 },
                          borderRadius: 999,
                          minWidth: 56,
                          opacity: disabled && !isSelected ? 0.4 : 1,
                        }}
                        onClick={() => !disabled && handleClickC(opt)}
                      >
                        {opt}
                      </Button>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* actions row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                startIcon={<ArticleIcon />}
                onClick={addToCart}
                disabled={adding}
                sx={{
                  fontSize: { xs: 14, md: 16 },
                  px: { xs: 2.4, md: 3 },
                  py: { xs: 0.7, md: 0.9 },
                  borderRadius: 999,
                }}
              >
                {adding ? t.addingBtn : t.inquiryBtn}
              </Button>

              {/* ✅ remove the old red inline message; dialog handles it */}

              {/* ✅ favourite button: HIDE when not logged in */}
              {showFavBtn && (
                <Tooltip title={favTooltip}>
                  <span>
                    <IconButton
                      onClick={toggleFavourite}
                      disabled={favBusy}
                      aria-label={favTooltip}
                      sx={{
                        borderRadius: 999,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        width: { xs: 42, md: 46 },
                        height: { xs: 42, md: 46 },
                        "&:hover": { bgcolor: "action.hover" },
                        "&:active": { transform: "scale(0.98)" },
                      }}
                    >
                      {fav ? (
                        <FavoriteIcon sx={{ color: "error.main" }} />
                      ) : (
                        <FavoriteBorderIcon sx={{ color: "text.secondary" }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {/* ✅ Login-required dialog for inquiry */}
      <Dialog
        open={loginOpen}
        onClose={closeLoginDialog}
        aria-labelledby="inquiry-login-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 420,
            width: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle
          id="inquiry-login-dialog-title"
          sx={{ fontWeight: 700, fontSize: { xs: 18, md: 22 } }}
        >
          {t.loginTitle}
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              whiteSpace: "pre-line",
              lineHeight: 1.7,
              fontWeight: 400,
              fontSize: { xs: 16, md: 18 },
            }}
          >
            {t.inquiryBtnMsg}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="outlined"
            onClick={closeLoginDialog}
            sx={{ borderRadius: 999, px: 2.5 }}
          >
            {t.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              closeLoginDialog();
              redirectToAuth();
            }}
            sx={{ borderRadius: 999, px: 2.8 }}
          >
            {t.login}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default React.memo(Top);
